import React, { useState, useEffect, lazy } from 'react';
import { Provider, useSelector } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import { BrowserRouter as Router, Navigate, Route, Routes } from 'react-router-dom';
import { ConfigProvider, Spin } from 'antd';
import { AuthProvider } from './context/KeycloakAuthProvider';
import { UserProvider } from './context/UserContext';
import { SessionProvider } from './context/SessionContext';
import { useUser } from './context/UserContext';
import ProtectedRoute from './components/utilities/protectedRoute';
import store from './redux/store';
import Auth from './routes/auth';
import config from './config/config';

import SuperAdmin from './routes/superAdminModules';
import GlobalAdmin from './routes/globalAdminModules';
import Admin from './routes/adminModules';
import Employee from './routes/employeeModules';
import Management from './routes/managementModules';
import Viewer from './routes/viewerModules';
import Supervisor from './routes/supervisorModules';
import ReceivingOfficer from './routes/receivingOfficerModules';
import Processor1 from './routes/processor-1';
import Processor2 from './routes/processor-2';
import ReportViewer from './routes/reportViewerModules';

import './static/css/style.css';
import 'antd/dist/antd.less';
import { loadActiveTheme } from './container/modules/settings/themes/themeService';
import { loadActiveLogo } from './container/modules/settings/logos/logoService';

loadActiveTheme();
loadActiveLogo();

const NotFound = lazy(() => import('./container/pages/404'));

const { theme } = config;

function ProviderConfig() {
  const { userDetails, isLoading, isLoggedIn } = useUser();
  const { rtl, topMenu, mainContent } = useSelector((state) => ({
    rtl: state.ChangeLayoutMode.rtlData,
    topMenu: state.ChangeLayoutMode.topMenu,
    mainContent: state.ChangeLayoutMode.mode,
  }));

  // const userRole = userDetails?.role;
  const userRole = localStorage.getItem('userRole');
  // console.log('userRole here in app', userRole2)

  if (isLoading) {
    return (
      <div
        style={{
          minHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Spin />
      </div>
    );
  }

  return (
    <ConfigProvider direction={rtl ? 'rtl' : 'ltr'}>
      <ThemeProvider theme={{ ...theme, rtl, topMenu, mainContent }}>
        <Router basename={process.env.PUBLIC_URL}>
          <Routes>
            {/* Public Routes */}
            {<Route path="/*" element={<Auth />} />}

            {/* Role-Specific Routes */}
            {
              <>
                {userRole === null || userRole === undefined ? (
                  <>{/* <Navigate to="/" replace /> */}</>
                ) : (
                  <>
                    {userRole === 'superadmin' && (
                      <>
                        <Route path="/" element={<Navigate to="/superadmin/" replace />} />
                        <Route path="/superadmin/*" element={<ProtectedRoute path="/*" Component={SuperAdmin} />} />
                      </>
                    )}
                    {userRole === 'globaladmin' && (
                      <>
                        <Route path="/" element={<Navigate to="/globaladmin/" replace />} />
                        <Route path="/globaladmin/*" element={<ProtectedRoute path="/*" Component={GlobalAdmin} />} />
                      </>
                    )}
                    {userRole === 'supervisor' && (
                      <>
                        <Route path="/" element={<Navigate to="/supervisor/" replace />} />
                        <Route path="/supervisor/*" element={<ProtectedRoute path="/*" Component={Supervisor} />} />
                      </>
                    )}
                    {userRole === 'admin' && (
                      <>
                        <Route path="/" element={<Navigate to="/admin/pages/search" replace />} />
                        <Route path="/admin/*" element={<ProtectedRoute path="/*" Component={Admin} />} />
                      </>
                    )}
                    {userRole === 'employee' && (
                      <>
                        <Route path="/" element={<Navigate to="/employee/" replace />} />
                        <Route path="/employee/*" element={<ProtectedRoute path="/*" Component={Employee} />} />
                      </>
                    )}
                    {userRole === 'management' && (
                      <>
                        <Route path="/" element={<Navigate to="/management/pages/search" replace />} />
                        <Route path="/management/*" element={<ProtectedRoute path="/*" Component={Management} />} />
                      </>
                    )}
                    {userRole === 'processor-1' && (
                      <>
                        <Route path="/" element={<Navigate to="/processor-1/" replace />} />
                        <Route path="/processor-1/*" element={<ProtectedRoute path="/*" Component={Processor1} />} />
                      </>
                    )}
                    {userRole === 'processor-2' && (
                      <>
                        <Route path="/" element={<Navigate to="/processor-2/" replace />} />
                        <Route path="/processor-2/*" element={<ProtectedRoute path="/*" Component={Processor2} />} />
                      </>
                    )}
                    {userRole === 'receiving-officer' && (
                      <>
                        <Route path="/" element={<Navigate to="/receiving-officer/" replace />} />
                        <Route
                          path="/receiving-officer/*"
                          element={<ProtectedRoute path="/*" Component={ReceivingOfficer} />}
                        />
                      </>
                    )}
                    {userRole === 'viewer' && (
                      <>
                        <Route path="/" element={<Navigate to="/viewer/pages/search" replace />} />
                        <Route path="/viewer/*" element={<ProtectedRoute path="/*" Component={Viewer} />} />
                      </>
                    )}
                    {userRole === 'report-viewer' && (
                      <>
                        <Route path="/" element={<Navigate to="/report-viewer/pages/report-generation" replace />} />
                        <Route
                          path="/report-viewer/*"
                          element={<ProtectedRoute path="/*" Component={ReportViewer} />}
                        />
                      </>
                    )}
                  </>
                )}
              </>
            }

            <Route path="*" element={<NotFound />} />
          </Routes>
        </Router>
      </ThemeProvider>
    </ConfigProvider>
  );
}

function App() {
  return (
    <Provider store={store}>
      <AuthProvider>
        <SessionProvider>
          <UserProvider>
            <ProviderConfig />
          </UserProvider>
        </SessionProvider>
      </AuthProvider>
    </Provider>
  );
}

export default App;
